import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import HeroSection from "./HeroSection";
import Categories from "./Categories";
import AdsSlider from "./AdsSlider";
import Steps from "./Steps";
import CTA from "./CTA";
import RecentlyListedSlider from "./RecentlyListedSlider";

export default function Homepage() {
  const [allAds, setAllAds] = useState([]);
  const [recentlySold, setRecentlySold] = useState([]);
  const [recentlyListedAds, setRecentlyListedAds] = useState([]);
  const [categories, setCategories] = useState([]);

  const [isLoadingAds, setIsLoadingAds] = useState(false);
  const [isLoadingSoldAds, setIsLoadingSoldAds] = useState(false);
  const [isLoadingRecentAds, setIsLoadingRecentAds] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const [adsError, setAdsError] = useState(null);
  const [soldAdsError, setSoldAdsError] = useState(null);
  const [recentAdsError, setRecentAdsError] = useState(null);
  const [categoriesError, setCategoriesError] = useState(null);

  const [categoriesWithAds, setCategoriesWithAds] = useState([]);
  const [loadingCategoriesAds, setLoadingCategoriesAds] = useState(false);
  const [categoriesAdsError, setCategoriesAdsError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_SERVER_BASE_URL;

  useEffect(() => {
    const fetchAllAds = async () => {
      setIsLoadingAds(true);
      try {
        const response = await fetch(`${API_BASE_URL}/homepageapis/all`);
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to fetch ads");
        setAllAds(data.data);
      } catch (error) {
        setAdsError(error.message);
      } finally {
        setIsLoadingAds(false);
      }
    };

    const fetchRecentlySold = async () => {
      setIsLoadingSoldAds(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/homepageapis/recentlySold`
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to fetch recently sold ads");
        setRecentlySold(data.data);
      } catch (error) {
        setSoldAdsError(error.message);
      } finally {
        setIsLoadingSoldAds(false);
      }
    };

    const fetchRecentlyListedAds = async () => {
      setIsLoadingRecentAds(true);
      try {
        const response = await fetch(`${API_BASE_URL}/homepageapis/recent`);
        const data = await response.json();
        if (!response.ok)
          throw new Error(
            data.message || "Failed to fetch recently listed ads"
          );
        setRecentlyListedAds(data.data);
      } catch (error) {
        setRecentAdsError(error.message);
      } finally {
        setIsLoadingRecentAds(false);
      }
    };

    const fetchCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to fetch categories");
        setCategories(data);
      } catch (error) {
        setCategoriesError(
          "Unable to load categories. Please try again later."
        );
      } finally {
        setIsLoadingCategories(false);
      }
    };

    const fetchCategoriesWithAds = async () => {
      setLoadingCategoriesAds(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/homepageapis/categories-with-ads`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch categories with ads");
        }
        const data = await response.json();
        setCategoriesWithAds(data.data); // Store only the 'data' array
      } catch (err) {
        setCategoriesAdsError(err.message);
      } finally {
        setLoadingCategoriesAds(false);
      }
    };

    fetchAllAds();
    fetchCategories();
    fetchRecentlySold();
    fetchRecentlyListedAds();
    fetchCategoriesWithAds();
  }, []);

  return (
    <>
      <Helmet>
        <title>Listed.ky - Buy & Sell in Cayman Islands</title>
        <meta
          name="description"
          content="Listed.ky is a premier online marketplace designed to make buying and selling simple, safe, and accessible for everyone. Founded on the principles of trust, transparency, and convenience."
        />
      </Helmet>
      <AnalyticsTracker />
      <HeroSection />
      <Categories
        categories={categories}
        isLoadingCategories={isLoadingCategories}
        categoriesError={categoriesError}
      />
      <RecentlyListedSlider
        ads={recentlyListedAds}
        isLoading={isLoadingRecentAds}
        error={recentAdsError}
        sectionTitle="Recently Listed"
        sectionSubTitle="Check out what's new"
        prevClass="custom-prev-listed"
        nextClass="custom-next-listed"
      />
      <Steps />
      <AdsSlider
        ads={recentlySold}
        isLoading={isLoadingSoldAds}
        error={soldAdsError}
        sectionTitle="Recently Sold"
        sectionSubTitle="These items have found a new home"
        prevClass="custom-prev-sold"
        nextClass="custom-next-sold"
      />
      {categoriesWithAds.map((categoryData, index) => (
        <AdsSlider
          ads={categoryData.ads}
          isLoading={loadingCategoriesAds}
          error={categoriesAdsError}
          sectionTitle={categoryData.category}
          sectionSubTitle={`Browse items in ${categoryData.category}`}
          prevClass={`custom-prev-${categoryData.category}`}
          nextClass={`custom-next-${categoryData.category}`}
        />
      ))}
      <CTA />
    </>
  );
}
